import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import {
    ApplyIncentiveToTheOrderMethod,
    DeleteIncentiveFromTheOrderMethod,
    getApplyIncentiveToTheOrderUrl,
    getDeleteIncentiveFromTheOrderUrl,
} from '@/shared/api/openApi';
import { type OrderDto } from '@/shared/api/order';
import { request } from '@/shared/api/request';
import { GiftCardApplyError, PromoCodeApplyError, PromoCodeRemoveError } from '../errors';
import type {
    ApplyIncentiveRequestParams,
    ApplyIncentiveResponse,
    RemoveIncentiveRequestParams,
    RemoveIncentiveResponse,
} from '../types';
import { upsertGetOrderQueryData } from './api';

const incentiveApi = createApi({
    name: 'entities/order/api/incentiveApi',
    baseQuery: createBaseQuery(request),
    endpoints: (builder) => {
        return {
            ApplyIncentive: builder.mutation<ApplyIncentiveResponse, ApplyIncentiveRequestParams>({
                query: (payload) => {
                    return {
                        url: getApplyIncentiveToTheOrderUrl(payload),
                        method: ApplyIncentiveToTheOrderMethod,
                        params: { signature: payload.signature },
                        body: { code: payload.code },
                    };
                },
                transformResponse: (response: ApplyIncentiveResponse, meta, arg) => {
                    if (response.is_success) {
                        upsertGetOrderQueryData(response.order, meta, {
                            order_id: arg.order_id,
                            signature: arg.signature,
                            store_slug: arg.store_slug,
                        });
                    }

                    return response;
                },
            }),

            RemoveIncentive: builder.mutation<RemoveIncentiveResponse, RemoveIncentiveRequestParams>({
                query: (payload) => {
                    return {
                        url: getDeleteIncentiveFromTheOrderUrl(payload),
                        method: DeleteIncentiveFromTheOrderMethod,
                        params: { signature: payload.signature },
                        body: { incentive_type: payload.incentive_type },
                    };
                },
                transformResponse: (response: RemoveIncentiveResponse, meta, arg) => {
                    if (response.is_success) {
                        upsertGetOrderQueryData(response.order, meta, {
                            order_id: arg.order_id,
                            signature: arg.signature,
                            store_slug: arg.store_slug,
                        });
                    }

                    return response;
                },
            }),
        };
    },
});

export async function applyOrderIncentive(params: ApplyIncentiveRequestParams): Promise<OrderDto> {
    const response = await incentiveApi.endpoints.ApplyIncentive.mutate(params);

    if (response.is_success) {
        return response.order;
    }

    switch (response.error) {
        case 'ERROR_GIFT_CARD_ALREADY_APPLIED':
            throw new GiftCardApplyError('Подарочная карта уже применена');
        case 'ERROR_GIFT_CARD_BALANCE_EXCEEDED':
            throw new GiftCardApplyError('Баланс подарочной карты исчерпан');
        case 'ERROR_GIFT_CARD_EXPIRED':
            throw new GiftCardApplyError('Подарочная карта просрочена');
        case 'ERROR_GIFT_CARD_NOT_ACTIVE':
            throw new GiftCardApplyError('Подарочная карта не активна');
        case 'ERROR_GIFT_CARD_NOT_ALLOWED_FOR_SELF_PICKUP':
            throw new GiftCardApplyError('Оплата подарочной картой недоступна для самовывоза');
        case 'ERROR_INCENTIVE_NOT_FOUND':
            throw new PromoCodeApplyError('Промокод не найден');
        case 'ERROR_INVALID_ORDER_STATUS':
            throw new PromoCodeApplyError('Не выполнены условия для применения промокода');
        case 'ERROR_MINIMUM_ORDER_AMOUNT_NOT_MET':
            throw new PromoCodeRemoveError('Минимальная сумма заказа не достигнута');
        case 'ERROR_ORDER_HAS_ANOTHER_APPLIED_PROMOCODE':
            throw new PromoCodeRemoveError('Вы уже применили другой промокод');
        case 'ERROR_PROMOCODE_HAS_NO_APPLICABLE_ITEMS':
            throw new PromoCodeApplyError('В корзине нет подходящих товаров');
        case 'ERROR_PROMOCODE_USAGE_LIMIT_EXCEEDED':
            throw new PromoCodeRemoveError('Лимит использования промокода исчерпан');

        default:
            throw new PromoCodeApplyError('Произошла ошибка при применении промокода');
    }
}

export async function removeOrderIncentive(params: RemoveIncentiveRequestParams): Promise<void> {
    await incentiveApi.endpoints.RemoveIncentive.mutate(params);
}
